import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Package, Lock, Download, CheckCircle2 } from "lucide-react";
import api, { publicApi, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/constants";
import { openSealedCapsule } from "../lib/clientVault";
import { useAuth } from "../context/AuthContext";
import { downloadAllAsZip } from "./CapsuleDetail";

import { _t } from "@/lib/i18n";
const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#070709] px-4 py-12">
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-10">
        <img src="/images/img1.png" alt={_t("Time Capsule")} className="w-10 h-10 rounded-lg" />
        <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
      </div>
      {children}
    </div>
  </div>
);

const Notice = ({ title, text, testId }) => (
  <div className="tc-card p-8 md:p-10 text-center" data-testid={testId}>
    <h1 className="font-display text-3xl text-white mb-3">{title}</h1>
    <p className="text-white/60">{text}</p>
  </div>
);

const Waiting = () => (
  <div className="text-white/60 flex items-center gap-2" data-testid="open-link-loading">
    <Loader2 className="w-5 h-5 animate-spin text-[#FF5722]" />{" "}{_t("Un momento…")}</div>
);

function AcceptBox({ info, onAccept, busy }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="tc-card p-8 md:p-10" data-testid="open-link-accept">
      <div className="flex items-center gap-3 mb-4"><Package className="w-6 h-6 text-[#FF5722]" />
        <h1 className="font-display text-3xl text-white">{_t("Una capsula del tempo per te")}</h1></div>
      <p className="text-white/80 mb-2">{_t("{0} ti ha affidato «{1}».", info.sender_name ? <strong>{info.sender_name}</strong> : _t("Qualcuno"), info.title)}</p>
      <p className="text-white/60 text-sm mb-6 flex items-center gap-2"><Lock className="w-4 h-4" />{" "}{_t("Il contenuto viene decifrato solo sul tuo dispositivo e scaricato in un file zip.")}</p>
      <label className="flex items-start gap-2 cursor-pointer mb-6">
        <input data-testid="open-link-consent" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 accent-[#FF5722]" />
        <span className="text-sm text-white/80">{_t("Confermo di essere il destinatario ({0}) e accetto la consegna. Da questo momento il link resterà valido per 48 ore, poi il contenuto verrà cancellato.", info.recipient_email)}</span>
      </label>
      <button data-testid="open-link-accept-btn" disabled={!ok || busy} onClick={onAccept} className="btn-launch">{_t("{0} Accetta e scarica", busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />)}</button>
    </div>
  );
}

function Downloaded({ info, onAgain }) {
  return (
    <div className="tc-card p-8 md:p-10 text-center" data-testid="open-link-downloaded">
      <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-4" />
      <h1 className="font-display text-3xl text-white mb-3">{_t("Capsula scaricata")}</h1>
      <p className="text-white/60 mb-6">{_t("Trovi «{0}» nei download del tuo dispositivo. {1}", info.title, info.expires_at && <> Puoi scaricarla di nuovo fino al {formatDateTime(info.expires_at)}.</>)}</p>
      <button data-testid="open-link-download-again" onClick={onAgain} className="btn-ghost"><Download className="w-4 h-4" />{" "}{_t("Scarica di nuovo")}</button>
    </div>
  );
}

function AccountRedirect({ info, token }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      nav(`/login?next=${encodeURIComponent(`/apri/${token}`)}&email=${encodeURIComponent(info.recipient_email)}`, { replace: true });
      return;
    }
    api.post(`/open/${token}/claim`)
      .then(({ data }) => nav(`/capsules/${data.capsule_id}`, { replace: true }))
      .catch((e) => setError(formatApiError(e)));
  }, [user, token, info, nav]);
  if (error) return <Notice testId="open-link-error" title={_t("Account diverso")} text={_t("{0}. Esci e accedi con {1}.", error, info.recipient_email)} />;
  return <Waiting />;
}

export default function OpenByLink() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = useCallback(async (data) => {
    setBusy(true);
    try {
      const result = await openSealedCapsule(publicApi, null, `/open/${token}/bundle`);
      await downloadAllAsZip(data.title, { contents: result.contents || [], finalMessage: result.final_message || "" });
      await publicApi.post(`/open/${token}/opened`);
      setDone(true);
    } catch (e) { setError(formatApiError(e)); }
    setBusy(false);
  }, [token]);

  useEffect(() => {
    publicApi.get(`/open/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post(`/open/${token}/accept`);
      setInfo(data);
      await download(data);
    } catch (e) { setError(formatApiError(e)); setBusy(false); }
  };

  const ready = info && ["DISPONIBILE", "APERTA"].includes(info.state);
  let body;
  if (error) body = <Notice testId="open-link-error" title={_t("Impossibile aprire la capsula")} text={error} />;
  else if (!info) body = <Waiting />;
  else if (info.has_account) body = <AccountRedirect info={info} token={token} />;
  else if (info.purged || info.expired) body = <Notice testId="open-link-expired" title={_t("Link scaduto")} text={_t("Le 48 ore dall'accettazione sono trascorse e il contenuto è stato cancellato.")} />;
  else if (!ready) body = <Notice testId="open-link-not-ready" title={_t("Non ancora")} text={_t("Questa capsula non è ancora disponibile.")} />;
  else if (done) body = <Downloaded info={info} onAgain={() => download(info)} />;
  else if (info.accepted_at) body = <Downloaded info={info} onAgain={() => download(info)} />;
  else body = <AcceptBox info={info} onAccept={accept} busy={busy} />;
  return <Shell>{body}</Shell>;
}
